import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FolderIcon } from "@heroicons/react/24/outline";
import { fetchProjectFolders } from "../../../api/project/folder";
import { PMEntityPicker } from "./PMEntityPicker";
import { IconAvatar, rowsFrom } from "./pickerParts";

/** Folders per project fetched in one request; search runs locally on the path. */
const FOLDERS_PER_PROJECT = 500;
const MAX_FOLDER_DEPTH = 20;

const folderName = (row) => row?.title ?? row?.displayTitle ?? row?.name ?? `#${row?.id}`;
const parentIdOf = (row) => row?.parent?.id ?? row?.parent_id ?? null;

/** Parent path names for each folder (cycle-safe). */
export function folderPaths(rows) {
  const byId = new Map(rows.map((row) => [String(row.id), row]));
  const paths = new Map();
  rows.forEach((row) => {
    const names = [];
    let parentId = parentIdOf(row);
    for (let depth = 0; parentId != null && depth < MAX_FOLDER_DEPTH; depth += 1) {
      const parent = byId.get(String(parentId));
      if (!parent) break;
      names.unshift(folderName(parent));
      parentId = parentIdOf(parent);
    }
    paths.set(String(row.id), names);
  });
  return paths;
}

export const folderToItem = (row, parentNames = []) =>
  row?.id == null
    ? null
    : {
        id: row.id,
        primary: folderName(row),
        secondary: parentNames.length ? parentNames.join(" / ") : null,
        leading: <IconAvatar icon={FolderIcon} />,
        object: row,
        searchText: [...parentNames, folderName(row)].join(" / ").toLowerCase(),
      };

export function folderItems(rows) {
  const paths = folderPaths(rows);
  return rows
    .map((row) => folderToItem(row, paths.get(String(row.id))))
    .sort((a, b) => a.searchText.localeCompare(b.searchText, "nb"));
}

/**
 * Project folder picker (new design). Requires a project. Emits the legacy
 * ProjectFolderSelector contract: `onChange({ target: { name, value }, object })`.
 */
export function ProjectFolderPicker({
  name = "folder_id",
  label,
  value,
  projectId,
  onChange,
  setFieldValue,
  disabled = false,
  error,
}) {
  const { t } = useTranslation();
  const cacheRef = useRef({ projectId: null, promise: null });
  const hasProject = projectId != null && projectId !== "";

  const loadFolders = useCallback(() => {
    if (cacheRef.current.projectId !== projectId || !cacheRef.current.promise) {
      const promise = fetchProjectFolders({
        project_id: projectId,
        query: "",
        page: 1,
        per_page: FOLDERS_PER_PROJECT,
        sortBy: "created_at",
        sort: "desc",
      }).then((response) => folderItems(rowsFrom(response)));
      // A failed load is retried on the next attempt instead of being cached.
      promise.catch(() => {
        if (cacheRef.current.promise === promise) cacheRef.current = { projectId: null, promise: null };
      });
      cacheRef.current = { projectId, promise };
    }
    return cacheRef.current.promise;
  }, [projectId]);

  const loadOptions = useCallback(
    async ({ query }) => {
      const items = await loadFolders();
      const needle = query.toLowerCase();
      return needle ? items.filter((item) => item.searchText.includes(needle)) : items;
    },
    [loadFolders]
  );

  const select = (item) => {
    const row = item?.object ?? null;
    const id = row?.id ?? null;
    setFieldValue?.(name, id);
    onChange?.({ target: { name, value: id }, object: row });
  };

  const folderProjectId = value?.project?.id ?? value?.project_id ?? projectId;
  const folderPath =
    value?.id != null && folderProjectId != null
      ? `/dashboard/projects/${folderProjectId}/folders/${value.id}`
      : null;

  return (
    <PMEntityPicker
      label={label ?? t("pickers.folder.label")}
      labelAction={folderPath ? { path: folderPath } : null}
      value={folderToItem(value)}
      placeholder={t(hasProject ? "pickers.folder.placeholder" : "pickers.folder.needsProject")}
      searchPlaceholder={t("pickers.folder.search")}
      disabled={disabled || !hasProject}
      error={error}
      loadOptions={loadOptions}
      onSelect={select}
      emptyText={t("pickers.folder.empty")}
    />
  );
}
